import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { setFeatureFlag, deleteFeatureFlag } from '../../services/setting/feature-flag.service'
import { updateFeatureFlag } from '../../store/actions/tenant.actions'
import {
  FEATURE_FLAG_CATALOG,
  CATALOG_KEYS
} from '../../components/Dashboard/Setting/School/FeatureFlags/catalog'

// MF-528 — reads the tenant's featureFlag from redux and exposes toggle/create/delete.
// The BE returns 204, so redux is updated optimistically after each successful call.
const useFeatureFlags = () => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const featureFlag = useSelector((s) => s?.tenant?.featureFlag || {})
  const featureFlagMeta = useSelector((s) => s?.tenant?.featureFlagMeta || {})
  const [busy, setBusy] = useState(null)

  // Known flags (from the catalogue) with their current per-tenant value.
  const known = useMemo(
    () => FEATURE_FLAG_CATALOG.map((f) => ({ ...f, value: !!featureFlag[f.key] })),
    [featureFlag]
  )

  // Custom flags = stored keys that aren't in the catalogue (description from featureFlagMeta).
  const custom = useMemo(
    () =>
      Object.keys(featureFlag)
        .filter((k) => !CATALOG_KEYS.has(k))
        .sort()
        .map((k) => ({ key: k, value: !!featureFlag[k], description: featureFlagMeta[k] || '' })),
    [featureFlag, featureFlagMeta]
  )

  // setFlag(key, value[, description]). A description (custom-flag create) is persisted and the
  // local meta is updated too; a plain toggle passes no description and leaves meta untouched.
  const setFlag = useCallback(
    async (key, value, description) => {
      setBusy(key)
      try {
        await setFeatureFlag(key, value, description)
        const nextMeta = description !== undefined ? { ...featureFlagMeta, [key]: description } : undefined
        dispatch(updateFeatureFlag({ ...featureFlag, [key]: value }, nextMeta))
        enqueueSnackbar(`Flag “${key}” ${value ? 'enabled' : 'disabled'}`, {
          variant: 'success'
        })
        return { error: false }
      } catch (e) {
        enqueueSnackbar(e?.message || 'Could not update the flag', {
          variant: 'error'
        })
        return { error: true, message: e?.message }
      } finally {
        setBusy(null)
      }
    },
    [dispatch, enqueueSnackbar, featureFlag, featureFlagMeta]
  )

  const removeFlag = useCallback(
    async (key) => {
      setBusy(key)
      try {
        await deleteFeatureFlag(key)
        const next = { ...featureFlag }
        delete next[key]
        const nextMeta = { ...featureFlagMeta }
        delete nextMeta[key]
        dispatch(updateFeatureFlag(next, nextMeta))
        enqueueSnackbar(`Flag “${key}” deleted`, { variant: 'success' })
        return { error: false }
      } catch (e) {
        enqueueSnackbar(e?.message || 'Could not delete the flag', {
          variant: 'error'
        })
        return { error: true, message: e?.message }
      } finally {
        setBusy(null)
      }
    },
    [dispatch, enqueueSnackbar, featureFlag, featureFlagMeta]
  )

  const exists = useCallback((key) => Object.prototype.hasOwnProperty.call(featureFlag, key), [featureFlag])

  return { known, custom, setFlag, removeFlag, exists, busy }
}

export default useFeatureFlags
